import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useDebounce } from '@/hooks/useDebounce'
import { useAuthStore } from '@/stores/authStore'
import { useGlobalSearch } from '@/features/search'
import { NAV_ITEMS, isNavVisible } from './navigation'
import { usePrivacySettings } from '@/hooks/usePrivacySettings'

interface Hit {
  key: string
  kind: string
  title: string
  detail: string
  to: string
}

const MAX_SCREENS = 4

/**
 * The search box in the top bar.
 *
 * <p>Screens are matched here, against the navigation labels — that is the frontend's own
 * knowledge and the server has no business holding it. Everything else comes from
 * {@code /v1/search}, which asks each module in turn and reports the categories this person
 * may not search rather than returning silence from them.
 *
 * <p>It is the same control on every page, with the same placeholder. A header search that
 * changes meaning with the route is a search nobody trusts; filtering the list in front of
 * you belongs to that list's own FilterBar.
 */
export function GlobalSearch() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const permissions = useAuthStore((state) => state.permissions)
  const isSuperAdmin = useAuthStore(
    (state) => state.user?.roles.includes('SUPER_ADMIN') ?? false)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const debounced = useDebounce(query, 250)
  const active = debounced.trim().length >= 2
  const directoryOpen = usePrivacySettings().employeeDirectoryVisible
  const maySearch = isSuperAdmin || permissions.has('search:global')
  const results = useGlobalSearch(debounced, active && maySearch)

  /** Screens the caller may actually open, matched on the words the menu shows. */
  const screens = useMemo<Hit[]>(() => {
    if (!active) return []
    const needle = debounced.trim().toLowerCase()
    const context = {
      has: (permission: string) => isSuperAdmin || permissions.has(permission),
      directoryOpen,
    }

    const hits: Hit[] = []
    for (const module of NAV_ITEMS) {
      const moduleLabel = t(`nav.${module.key}`)
      if (module.to && isNavVisible(module, context)
          && moduleLabel.toLowerCase().includes(needle)) {
        hits.push({
          key: `module-${module.key}`,
          kind: 'Screen',
          title: moduleLabel,
          detail: 'Module',
          to: module.to,
        })
      }
      for (const child of module.children ?? []) {
        const label = t(`navChild.${child.key}`)
        if (isNavVisible(module, context) && isNavVisible(child, context)
            && label.toLowerCase().includes(needle)) {
          hits.push({
            key: `child-${child.key}`,
            kind: 'Screen',
            title: label,
            detail: moduleLabel,
            to: child.to,
          })
        }
      }
    }
    return hits.slice(0, MAX_SCREENS)
  }, [active, debounced, permissions, isSuperAdmin, directoryOpen, t])

  const fromServer: Hit[] = (results.data?.hits ?? []).map((hit) => ({
    key: `${hit.category}-${hit.id}`,
    kind: hit.category,
    title: hit.title,
    detail: [hit.reference, hit.subtitle].filter(Boolean).join(' · '),
    to: hit.to,
  }))

  const hits = [...screens, ...fromServer]

  function go(to: string) {
    setOpen(false)
    setQuery('')
    void navigate(to)
  }

  return (
    <div
      className="relative hidden w-full max-w-sm lg:block"
      onFocus={() => { clearTimeout(blurTimer.current); setOpen(true) }}
      onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 120) }}
    >
      <Search
        className="pointer-events-none absolute start-0 top-1/2 ms-3 size-4 -translate-y-1/2 text-[var(--color-muted)]"
        aria-hidden="true"
      />
      <Input
        type="search"
        role="combobox"
        aria-expanded={open && hits.length > 0}
        aria-controls="global-search-results"
        autoComplete="off"
        value={query}
        onChange={(event) => { setQuery(event.target.value); setOpen(true) }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && hits[0]) go(hits[0].to)
          if (event.key === 'Escape') setOpen(false)
        }}
        placeholder={t('common.globalSearch')}
        aria-label={t('common.globalSearch')}
        className="rounded-full bg-[var(--color-page)] ps-9"
      />

      {open && active && (
        <ul
          id="global-search-results"
          role="listbox"
          className="absolute z-50 mt-1 max-h-96 w-full overflow-y-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] shadow-[var(--shadow-popover)]"
        >
          {hits.length === 0 && (
            <li className="px-3 py-2 text-sm text-[var(--color-muted)]">
              {results.isFetching ? 'Searching…' : 'Nothing matches that'}
            </li>
          )}
          {hits.map((hit) => (
            <li key={hit.key} role="option" aria-selected="false">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-[var(--color-page)]"
                onMouseDown={(event) => { event.preventDefault(); go(hit.to) }}
              >
                <span className="min-w-0">
                  <span className="block truncate text-[var(--color-heading)]">{hit.title}</span>
                  <span className="block truncate text-xs text-[var(--color-muted)]">
                    {hit.detail}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-[var(--color-muted)]">
                  {hit.kind}
                </span>
              </button>
            </li>
          ))}
          {(results.data?.skipped.length ?? 0) > 0 && (
            <li className="border-t border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-muted)]">
              Not searched: {results.data?.skipped.join('; ')}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
